import { useMutation, useQuery } from "@tanstack/react-query";

import { api, queryClient, type AppConfig, type BillingItem, type BillingStatus } from "@/lib/api";
import { keys } from "@/hooks/queries";

/** The user's live entitlement/subscription status. Works even when billing is
 * disabled for this deployment (`enabled: false`) — the studio should read
 * that flag rather than inferring "off" from a fetch failure. A short
 * `staleTime` keeps it fresh without polling: entitlements only change after
 * a checkout/portal round-trip or a webhook, both of which explicitly
 * invalidate this key (see the mutations below). */
export function useBilling() {
  return useQuery({
    queryKey: keys.billing,
    queryFn: () => api.request<BillingStatus>("GET", "/api/billing/status"),
    staleTime: 60_000,
  });
}

/** Public client bootstrap config: feature flags + display-only pricing.
 * Effectively static for the lifetime of a session (a deploy that changes it
 * ships a new client), hence `staleTime: Infinity` — refetch only on an
 * explicit `invalidateQueries` (e.g. after a version-mismatch reload). */
export function useAppConfig() {
  return useQuery({
    queryKey: keys.config,
    queryFn: () => api.request<AppConfig>("GET", "/api/config"),
    staleTime: Infinity,
  });
}

/** Start a Stripe Checkout session for one or both add-ons and return the
 * hosted URL to redirect to. Does NOT navigate itself — callers decide when
 * (see `components/feedback/use-recovery-action.ts` for the shared "click
 * Upgrade → redirect" wiring used by RestrictedState/ErrorState). */
export function useCheckout() {
  return useMutation({
    mutationFn: (items: BillingItem[]) =>
      api.request<{ url: string }>("POST", "/api/billing/checkout", { items }),
  });
}

/** Reconcile the live subscription's add-ons with a desired end state. Mirrors
 * `/billing/update`'s two possible shapes: a fresh `{checkout_url}` when there
 * was no subscription to modify yet, or the updated {@link BillingStatus}
 * otherwise (patched into the cache immediately so the UI reflects it without
 * a round-trip). */
export function useUpdateSubscription() {
  return useMutation({
    mutationFn: (body: { storage: boolean; models: boolean }) =>
      api.request<{ checkout_url: string } | BillingStatus>("POST", "/api/billing/update", body),
    onSuccess: (res) => {
      if (!("checkout_url" in res)) {
        queryClient.setQueryData(keys.billing, res);
      }
      void queryClient.invalidateQueries({ queryKey: keys.billing });
    },
  });
}

/** Open the Stripe customer portal (manage/cancel) and return its URL. */
export function useBillingPortal() {
  return useMutation({
    mutationFn: () => api.request<{ url: string }>("POST", "/api/billing/portal"),
  });
}
